import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ConfigProvider, theme as antdTheme } from "antd";
import ptBR from "antd/locale/pt_BR";
import { AuthProvider } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";
import { GoogleMapsProvider } from "./contexts/GoogleMapsContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/common/ProtectedRoute";
import AdminRoute from "./components/common/AdminRoute";
import TecnicoRoute from "./components/common/TecnicoRoute";
import TenantGuard from "./components/common/TenantGuard";
import Login from "./pages/Login";
import DefaultHomeRedirect from "./components/common/DefaultHomeRedirect";
import "./App.css";

// Lazy loading de páginas para reduzir o bundle inicial
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Gestao = lazy(() => import("./pages/Gestao"));
const NucleosCatalogos = lazy(() => import("./pages/NucleosCatalogos"));
const Matriculas = lazy(() => import("./pages/Matriculas"));
const GerenciamentoMatriculas = lazy(() => import("./pages/GerenciamentoMatriculas"));
const Operacoes = lazy(() => import("./pages/Operacoes"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const AnosLetivos = lazy(() => import("./pages/AnosLetivos"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const Monitores = lazy(() => import("./pages/Monitores"));
const HistoricoAluno = lazy(() => import("./pages/HistoricoAluno"));
const Planejamentos = lazy(() => import("./pages/Planejamentos"));
const Usuarios = lazy(() => import("./pages/Usuarios"));
const Escolas = lazy(() => import("./pages/Escolas"));
const Galeria = lazy(() => import("./pages/Galeria"));
const Ocorrencias = lazy(() => import("./pages/Ocorrencias"));
const PermissoesAdmin = lazy(() => import("./pages/PermissoesAdmin"));

// Componente de loading para Suspense
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Carregando...</p>
    </div>
  </div>
);

// Componente interno que usa o tema para configurar o Ant Design
const AppContent = () => {
  const { isDark } = useTheme();

  return (
    <ConfigProvider
      locale={ptBR}
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: "#021f57",
        },
      }}
    >
      <GoogleMapsProvider>
        <AuthProvider>
          <ToastProvider>
            <Router>
              <TenantGuard>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Rotas públicas */}
                    <Route path="/login" element={<Login />} />

                    {/* Rotas protegidas com layout */}
                    <Route element={<ProtectedRoute />}>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/gestao" element={<Gestao />} />
                      <Route path="/nucleos-atividades" element={<NucleosCatalogos />} />
                      <Route path="/matriculas" element={<Matriculas />} />
                      <Route path="/gerenciamento-matriculas" element={<GerenciamentoMatriculas />} />
                      <Route path="/operacoes" element={<Operacoes />} />
                      <Route path="/perfil" element={<UserProfile />} />
                      <Route path="/anos-letivos" element={<AnosLetivos />} />
                      <Route path="/relatorios" element={<Relatorios />} />
                      <Route path="/monitores" element={<Monitores />} />
                      <Route path="/historico-aluno" element={<HistoricoAluno />} />
                      <Route path="/planejamentos" element={<Planejamentos />} />
                      <Route path="/galeria" element={<Galeria />} />
                      <Route path="/ocorrencias" element={<Ocorrencias />} />
                    </Route>

                    {/* Rotas protegidas apenas para ADMIN */}
                    <Route
                      path="/usuarios"
                      element={
                        <AdminRoute>
                          <Usuarios />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/escolas"
                      element={
                        <AdminRoute>
                          <Escolas />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/permissoes"
                      element={
                        <TecnicoRoute>
                          <PermissoesAdmin />
                        </TecnicoRoute>
                      }
                    />
                    <Route path="/" element={<DefaultHomeRedirect />} />
                    <Route path="*" element={<DefaultHomeRedirect />} />
                  </Routes>
                </Suspense>
              </TenantGuard>
            </Router>
          </ToastProvider>
        </AuthProvider>
      </GoogleMapsProvider>
    </ConfigProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
